import { useSessionKey } from "@auth/sessionStore";
import { roomAreasClient } from "@rooms/roomAreasClient";
import type { Room } from "@rooms/roomsClient";
import { useRoomBaseline } from "@rooms/roomsQueries";
import { Button, Dialog, FieldError } from "@scribenote/brian-design-system";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import type { MapDocument } from "./clinicMapClient";
import { useHeldStill, useRoomDoorAreas } from "./clinicMapQueries";
import { doorLabels, type RoomsById } from "./mapWords";
import { type AreaPicture, RoomAreasEditor } from "./RoomAreasEditor";

export const RoomAreasDialog = ({
  room,
  doc,
  rooms,
  onClose,
}: {
  room: Room;
  doc: MapDocument;
  rooms: RoomsById;
  onClose: () => void;
}) => {
  const sessionKey = useSessionKey();
  const baseline = useRoomBaseline(sessionKey, room.id, true);
  const still = useHeldStill(sessionKey, room.id);
  const doors = useRoomDoorAreas(sessionKey, room.id);
  const queryClient = useQueryClient();
  const queryKey = ["room-camera-areas", sessionKey, room.id];
  const [freshAreasLoaded, setFreshAreasLoaded] = useState(false);
  const existing = useQuery({
    queryKey,
    queryFn: () => roomAreasClient.get(room.id),
    refetchOnMount: "always",
  });
  if (!freshAreasLoaded && existing.isFetchedAfterMount && existing.isSuccess)
    setFreshAreasLoaded(true);
  const loading =
    baseline.isPending ||
    still.isPending ||
    (!freshAreasLoaded && (existing.isFetching || !existing.isError));
  const error =
    (baseline.isError && baseline.data === undefined) ||
    (still.isError && still.data === undefined) ||
    (existing.isError && (!freshAreasLoaded || existing.data === undefined));
  if (loading || error)
    return (
      <Dialog
        open
        title={`Areas of ${room.name}`}
        onClose={onClose}
        footer={
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        }
      >
        {loading ? (
          <p className="text-sm text-annotation">Loading camera areas…</p>
        ) : (
          <div className="flex flex-col gap-2">
            <FieldError>
              Couldn’t load the saved areas or camera pictures.
            </FieldError>
            <Button
              variant="outline"
              onClick={() =>
                void Promise.all([
                  baseline.refetch(),
                  still.refetch(),
                  existing.refetch(),
                ])
              }
            >
              Try again
            </Button>
          </div>
        )}
      </Dialog>
    );
  const pictures: AreaPicture[] = [];
  if (baseline.data)
    pictures.push({
      source: "baseline",
      url: baseline.data.url,
      reference: null,
      expectedBaselineAt: baseline.data.capturedAt,
    });
  if (still.data)
    pictures.push({
      source: "still",
      url: still.data.url,
      reference: still.data.reference ?? null,
    });
  const space = doc.spaces.find((s) => s.room_uuid === room.id);
  const labels = space
    ? doorLabels(doc, space, rooms)
    : new Map<string, string>();
  const doorReference = doors.data
    ? {
        ...doors.data,
        doors: doors.data.doors
          .filter((d) => labels.has(d.doorUuid))
          .map((d) => ({
            key: d.doorUuid,
            label: labels.get(d.doorUuid)!,
            box: d.box,
          })),
      }
    : null;
  return (
    <RoomAreasEditor
      roomName={room.name}
      pictures={pictures}
      saved={existing.data ?? null}
      doorReference={doorReference}
      doorReferenceNotice={
        doors.isPending
          ? "Loading saved doors…"
          : doors.isError
            ? "Couldn’t load saved doors. Close and reopen this editor to try again."
            : undefined
      }
      onClose={onClose}
      onRefresh={async () => {
        const result = await still.refetch();
        if (result.error) throw result.error;
      }}
      onSave={async (change) => {
        await roomAreasClient.set(room.id, change);
        await queryClient.invalidateQueries({ queryKey });
      }}
    />
  );
};
